import * as actionTypes from "./constants";
import * as H from "history";
import {
  CancellationSummary,
  IPersistentLodgingTrackingProperties,
  Lodging,
  LodgingTokenResponse,
  RoomInfoProducts,
  TrackingProperties,
} from "redmond";
import {
  PremierCollectionShopCallState,
  PremierCollectionShopCallError,
  PremierCollectionShopStep,
} from "../reducer";

export interface IFetchPremierCollectionShop {
  type: actionTypes.FETCH_PREMIER_COLLECTION_SHOP;
  history: H.History;
  options?: {
    overrideStateByQueryParams?: boolean;
    forceCallHotelAvailability?: boolean;
    fetchSimilarHotels?: boolean;
    includeRoomsCount?: boolean;
  };
}

export const fetchPremierCollectionShop = (
  history: H.History,
  options?: {
    overrideStateByQueryParams?: boolean;
    forceCallHotelAvailability?: boolean;
    fetchSimilarHotels?: boolean;
    includeRoomsCount?: boolean;
  }
): IFetchPremierCollectionShop => ({
  type: actionTypes.FETCH_PREMIER_COLLECTION_SHOP,
  history,
  options,
});

export interface ISetPremierCollectionShopResults {
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_RESULTS;
  premierCollectionShopCallState: PremierCollectionShopCallState;
  premierCollectionShopCallError: PremierCollectionShopCallError | null;
  payload?: ISetPremierCollectionShopResultsArgs;
}

export interface ISetPremierCollectionShopResultsArgs {
  roomInfoProducts: RoomInfoProducts[];
  trackingPropertiesV2?: TrackingProperties;
  cancellationSummary?: CancellationSummary;
}

export const setPremierCollectionShopResults = (args: {
  premierCollectionShopCallState: PremierCollectionShopCallState;
  premierCollectionShopCallError?: PremierCollectionShopCallError;
  payload?: ISetPremierCollectionShopResultsArgs;
}): ISetPremierCollectionShopResults => ({
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_RESULTS,
  ...{ premierCollectionShopCallError: null, ...args },
});

interface IISetPremierCollectionShopCallStateFailedPayload {
  premierCollectionShopCallError: PremierCollectionShopCallError;
}
export interface ISetPremierCollectionShopCallStateFailed
  extends IISetPremierCollectionShopCallStateFailedPayload {
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_CALL_STATE_FAILED;
}

export const setPremierCollectionShopCallStateFailed = ({
  premierCollectionShopCallError,
}: IISetPremierCollectionShopCallStateFailedPayload): ISetPremierCollectionShopCallStateFailed => ({
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_CALL_STATE_FAILED,
  premierCollectionShopCallError,
});

export interface ISelectRoomType {
  type: actionTypes.SELECT_ROOM_TYPE;
  roomInfoIndex: number | null;
  productIndex: number | null;
}

export const selectRoomType = (
  roomInfoIndex: number | null,
  productIndex: number | null
): ISelectRoomType => {
  return {
    type: actionTypes.SELECT_ROOM_TYPE,
    roomInfoIndex,
    productIndex,
  };
};

export interface ISelectLodging {
  type: actionTypes.SELECT_LODGING;
  lodging: Lodging;
}

export const selectLodging = (lodging: Lodging): ISelectLodging => ({
  type: actionTypes.SELECT_LODGING,
  lodging,
});

export interface IAcknowledgePremierCollectionShopFailure {
  type: actionTypes.ACKNOWLEDGE_PREMIER_COLLECTION_SHOP_FAILURE;
}

export const acknowledgePremierCollectionShopFailure =
  (): IAcknowledgePremierCollectionShopFailure => ({
    type: actionTypes.ACKNOWLEDGE_PREMIER_COLLECTION_SHOP_FAILURE,
  });

export interface ISetPremierCollectionShopProgress {
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_PROGRESS;
  progress: PremierCollectionShopStep;
}

export const setPremierCollectionShopProgress = (
  progress: PremierCollectionShopStep
): ISetPremierCollectionShopProgress => ({
  type: actionTypes.SET_PREMIER_COLLECTION_SHOP_PROGRESS,
  progress,
});

export interface ISetIsFromHotelAvailability {
  type: actionTypes.SET_IS_FROM_HOTEL_AVAILABILITY;
  isFromHotelAvailability: boolean;
}

export const setIsFromHotelAvailability = (
  isFromHotelAvailability: boolean
): ISetIsFromHotelAvailability => ({
  type: actionTypes.SET_IS_FROM_HOTEL_AVAILABILITY,
  isFromHotelAvailability,
});

export interface ISetIsFromPremiumOffers {
  type: actionTypes.SET_IS_FROM_PREMIUM_OFFERS;
  isFromPremiumOffers: boolean;
}

export const setIsFromPremiumOffers = (
  isFromPremiumOffers: boolean
): ISetIsFromPremiumOffers => ({
  type: actionTypes.SET_IS_FROM_PREMIUM_OFFERS,
  isFromPremiumOffers,
});

export interface ISetCacheLodgingTokenResponse {
  type: actionTypes.SET_CACHE_LODGING_TOKEN_RESPONSE;
  lodgingTokenResponse: LodgingTokenResponse;
}

export const setCacheLodgingTokenResponse = (
  lodgingTokenResponse: LodgingTokenResponse
): ISetCacheLodgingTokenResponse => ({
  type: actionTypes.SET_CACHE_LODGING_TOKEN_RESPONSE,
  lodgingTokenResponse,
});

export interface ISetPersistentLodgingTrackingProperties {
  type: actionTypes.SET_PERSISTENT_LODGING_TRACKING_PROPERTIES;
  persistentLodgingTrackingProperties?: IPersistentLodgingTrackingProperties;
}

export const setPersistentLodgingTrackingProperties = (
  persistentLodgingTrackingProperties?: IPersistentLodgingTrackingProperties
): ISetPersistentLodgingTrackingProperties => ({
  type: actionTypes.SET_PERSISTENT_LODGING_TRACKING_PROPERTIES,
  persistentLodgingTrackingProperties,
});

export interface ISetSelectedLodgingIsFavorited {
  type: actionTypes.SET_SELECTED_LODGING_IS_FAVORITED;
  selectedLodgingIsFavorited?: boolean;
}
export const setSelectedLodgingIsFavorited = (
  selectedLodgingIsFavorited?: boolean
): ISetSelectedLodgingIsFavorited => ({
  type: actionTypes.SET_SELECTED_LODGING_IS_FAVORITED,
  selectedLodgingIsFavorited,
});

export interface ISetHotelDetailsEntrySource {
  type: actionTypes.SET_HOTEL_DETAILS_ENTRY_SOURCE;
  hotelDetailsEntrySource: string;
}

export const setHotelDetailsEntrySource = (
  hotelDetailsEntrySource: string
): ISetHotelDetailsEntrySource => ({
  type: actionTypes.SET_HOTEL_DETAILS_ENTRY_SOURCE,
  hotelDetailsEntrySource,
});

export interface ISetAllowedPayLaterLodgingIds {
  type: actionTypes.SET_ALLOWED_PAY_LATER_LODGING_IDS;
  allowedPayLaterLodgingIds: string[];
}

export const setAllowedPayLaterLodgingIds = (
  allowedPayLaterLodgingIds: string[]
): ISetAllowedPayLaterLodgingIds => ({
  type: actionTypes.SET_ALLOWED_PAY_LATER_LODGING_IDS,
  allowedPayLaterLodgingIds,
});

export interface ICheckLifestyleEligibility {
  type: actionTypes.CHECK_LIFESTYLE_ELIGIBILITY;
}

export const checkLifestyleEligibility =
  (): ICheckLifestyleEligibility => ({
    type: actionTypes.CHECK_LIFESTYLE_ELIGIBILITY,
  });

export interface ISetLifestyleEligibility {
  type: actionTypes.SET_LIFESTYLE_ELIGIBILITY;
  isEligible: boolean;
}

export const setLifestyleEligibility = (
  isEligible: boolean
): ISetLifestyleEligibility => ({
  type: actionTypes.SET_LIFESTYLE_ELIGIBILITY,
  isEligible,
});


export type PremierCollectionShopActions =
  | IFetchPremierCollectionShop
  | ISetPremierCollectionShopResults
  | ISetPremierCollectionShopCallStateFailed
  | ISelectRoomType
  | ISelectLodging
  | IAcknowledgePremierCollectionShopFailure
  | ISetPremierCollectionShopProgress
  | ISetIsFromHotelAvailability
  | ISetCacheLodgingTokenResponse
  | ISetIsFromPremiumOffers
  | ISetPersistentLodgingTrackingProperties
  | ISetSelectedLodgingIsFavorited
  | ISetHotelDetailsEntrySource
  | ISetAllowedPayLaterLodgingIds
  | ICheckLifestyleEligibility
  | ISetLifestyleEligibility;
